import { tokens } from '@/design';

export type LinePattern =
  | 'solid'
  | 'dash'
  | 'dot'
  | 'dash-dot'
  | 'long-dash'
  | 'cased'
  | 'arrow';
export type LineCap = 'butt' | 'round' | 'square';
export type LineJoin = 'bevel' | 'round' | 'miter';

export interface LineSymbolState {
  readonly presetId: string;
  readonly pattern: LinePattern;
  readonly color: string;
  readonly width: number;
  readonly opacity: number;
  readonly casingColor: string;
  readonly casingWidth: number;
  readonly cap: LineCap;
  readonly join: LineJoin;
}

export interface LineSymbolPreset {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly symbol: Omit<LineSymbolState, 'color' | 'width' | 'opacity' | 'casingColor'>;
}

export const DEFAULT_LINE_SYMBOL: LineSymbolState = {
  presetId: 'solid',
  pattern: 'solid',
  color: tokens.color.accent.primary,
  width: 2,
  opacity: 1,
  casingColor: tokens.color.surface[0],
  casingWidth: 2,
  cap: 'round',
  join: 'round',
};

export const LINE_SYMBOL_PRESETS: ReadonlyArray<LineSymbolPreset> = [
  {
    id: 'solid',
    label: 'Solid',
    description: 'Continuous line',
    symbol: { presetId: 'solid', pattern: 'solid', casingWidth: 2, cap: 'round', join: 'round' },
  },
  {
    id: 'dash',
    label: 'Dashed',
    description: 'Regular dashed line',
    symbol: { presetId: 'dash', pattern: 'dash', casingWidth: 2, cap: 'butt', join: 'round' },
  },
  {
    id: 'dot',
    label: 'Dotted',
    description: 'Round dotted line',
    symbol: { presetId: 'dot', pattern: 'dot', casingWidth: 2, cap: 'round', join: 'round' },
  },
  {
    id: 'dash-dot',
    label: 'Dash dot',
    description: 'Alternating dash and dot',
    symbol: { presetId: 'dash-dot', pattern: 'dash-dot', casingWidth: 2, cap: 'round', join: 'round' },
  },
  {
    id: 'long-dash',
    label: 'Long dash',
    description: 'Long cartographic dashes',
    symbol: { presetId: 'long-dash', pattern: 'long-dash', casingWidth: 2, cap: 'butt', join: 'round' },
  },
  {
    id: 'cased',
    label: 'Cased road',
    description: 'Wide casing behind the primary stroke',
    symbol: { presetId: 'cased', pattern: 'cased', casingWidth: 3, cap: 'round', join: 'round' },
  },
  {
    id: 'arrow',
    label: 'Directional arrows',
    description: 'Repeated arrows showing line direction',
    symbol: { presetId: 'arrow', pattern: 'arrow', casingWidth: 2, cap: 'round', join: 'round' },
  },
];

export function applyLineSymbolPreset(
  current: LineSymbolState,
  presetId: string,
): LineSymbolState {
  const preset = LINE_SYMBOL_PRESETS.find((entry) => entry.id === presetId)
    ?? LINE_SYMBOL_PRESETS[0]!;
  return { ...current, ...preset.symbol };
}

export function lineDashArray(pattern: LinePattern): ReadonlyArray<number> | null {
  if (pattern === 'dash') return [4, 3];
  if (pattern === 'dot') return [0.1, 2.5];
  if (pattern === 'dash-dot') return [5, 2, 0.1, 2];
  if (pattern === 'long-dash') return [8, 3];
  return null;
}

export function lineSymbolSvgDataUrl(symbol: LineSymbolState, width = 72, height = 30): string {
  const y = height / 2;
  const dash = lineDashArray(symbol.pattern);
  const lineCap = symbol.cap === 'butt' ? 'butt' : symbol.cap;
  const casing = symbol.pattern === 'cased'
    ? `<line x1="4" y1="${y}" x2="${width - 4}" y2="${y}" stroke="${escapeXml(symbol.casingColor)}" stroke-width="${symbol.width + symbol.casingWidth * 2}" stroke-linecap="${lineCap}"/>`
    : '';
  const arrow = symbol.pattern === 'arrow'
    ? `<path d="M ${width * 0.58} ${y - 5} L ${width * 0.7} ${y} L ${width * 0.58} ${y + 5}" fill="none" stroke="${escapeXml(symbol.color)}" stroke-width="${Math.max(1, symbol.width)}"/>`
    : '';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${casing}<line x1="4" y1="${y}" x2="${width - 4}" y2="${y}" stroke="${escapeXml(symbol.color)}" stroke-opacity="${symbol.opacity}" stroke-width="${symbol.width}" stroke-linecap="${lineCap}"${dash ? ` stroke-dasharray="${dash.join(' ')}"` : ''}/>${arrow}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function escapeXml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}
